import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Input } from "../../components/ui/input";
import { useLogin } from "../../adapters/Requests";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form.jsx";
import { toast } from "sonner";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { LoginSchema } from "../../schema.js";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import LoadingAnimation from "../../utils/LoadingAnimation.jsx";

function LoginForm() {
  const getErrorMessage = (error) => {
    const responseData = error?.response?.data;

    if (typeof responseData === "string") {
      return responseData;
    }

    if (responseData && typeof responseData === "object") {
      return (
        responseData.message ||
        responseData.error ||
        "Invalid Credentials. Please try again."
      );
    }

    return error?.message || "Invalid Credentials. Please try again.";
  };

  const form = useForm({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const {
    formState: { isSubmitting },
  } = form;

  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const [passwordVisible, setPasswordVisible] = useState(false); // State to control password visibility
  const loginSectionMode = (
    import.meta.env.VITE_APPROVAL_LOGIN_SECTION_MODE || "both"
  )
    .toLowerCase()
    .trim();
  const showLocalLogin = ["local", "both"].includes(loginSectionMode);
  const showSsoLogin = ["sso", "both"].includes(loginSectionMode);

  const loginMutation = useLogin();

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const exchangeToken = async () => {
      if (isAuthenticated && accounts.length > 0) {
        const { idToken, name, username } = accounts[0];

        // Send ID token to your backend
        setLoading(true);
        try {
          await loginMutation.mutateAsync({
            idToken,
            name,
            username,
            isAzureADLogin: true,
          });
          toast.success("Logged in successfully!");
        } catch (error) {
          const message = getErrorMessage(error);
          toast.error(message);
          console.error("Error submitting data:", error);
        }
        setLoading(false);
      }
    };

    exchangeToken();
  }, [isAuthenticated, accounts, instance]);

  const onSubmit = async (data) => {
    try {
      await loginMutation.mutateAsync(data);
      toast.success("Logged in successfully!");
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      console.error("Error submitting data:", error);
      setPasswordError(true);
      setTimeout(() => setPasswordError(false), 3000);
      form.reset();
    }
  };

  const handleAzureLogin = async () => {
    if (inProgress !== "none") {
      return;
    }
    setLoading(true);
    try {
      await instance.loginPopup({
        scopes: ["user.read"],
        prompt: "select_account",
      });
    } catch (error) {
      console.error("Azure login failed", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showLocalLogin && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="peer text-black">
                    Email Address
                  </FormLabel>
                  <Input
                    autoComplete="email"
                    placeholder="Enter your Email Address"
                    className={`peer-data-[state=error]:ring-rose-500 `}
                    {...field}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />{" "}
            <br />
            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="peer text-black">Password</FormLabel>
                  <div className="relative">
                    <Input
                      autoComplete="password"
                      placeholder="Enter your password"
                      type={passwordVisible ? "text" : "password"}
                      className={`peer-data-[state=error]:ring-rose-500 ${
                        passwordError ? "border-red-500" : ""
                      }`}
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                    >
                      {passwordVisible ? (
                        <Eye size={24} />
                      ) : (
                        <EyeSlash size={24} />
                      )}
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <br />
            {/* {alert.show && (
              <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
                <Alert severity={alert.severity}>{alert.message}</Alert>
              </Stack>
            )} */}
            <div>
              <button
                className="w-[360px] h-[44px] mt-6 border border-[#046f42] rounded-[8px] gap-8 text-center text-base leading-6 text-white bg-[#046f42] shadow-[0_12px_28px_rgba(4,111,66,0.35)] font-semibold tracking-[0.01em] relative overflow-hidden hover:bg-[#03633b] transition-colors duration-200 ease-in-out disabled:bg-[#03633b] disabled:text-white disabled:cursor-not-allowed"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? <LoadingAnimation /> : "Sign in"}
              </button>
            </div>
          </form>
        </Form>
      )}

      {showSsoLogin && (
        <div className="space-y-5 w-full">
          {/* {!showLocalLogin && alert.show && (
            <Stack sx={{ width: "fit-content", mb: 2 }} spacing={2}>
              <Alert severity={alert.severity}>{alert.message}</Alert>
            </Stack>
          )} */}
          <div className="h-16 mx-6">
            <button
              disabled={loading}
              onClick={handleAzureLogin}
              className="h-11 w-full mt-4 px-6 border border-[#046f42] rounded-[8px] gap-8 text-center text-base leading-6 text-white bg-[#046f42] shadow-[0_12px_28px_rgba(4,111,66,0.35)] font-semibold tracking-[0.01em] relative overflow-hidden hover:bg-[#03633b] transition-colors duration-200 ease-in-out disabled:bg-[#03633b] disabled:text-white disabled:cursor-not-allowed"
            >
              {loading ? <LoadingAnimation /> : "Sign in with NNPC SSO"}
            </button>
          </div>

          <div className="text-center text-sm text-gray-500">
            <span>Need help?</span>
            <a
              href="/help"
              className="ml-2 text-[#226845] font-medium hover:underline hover:text-[#1a4f34]"
            >
              Contact Support
            </a>
          </div>
        </div>
      )}
    </>
  );
}

export default LoginForm;
